import * as React from 'react';
import { CalendarSurfaceStore } from 'owa-calendar-surface-dategrid';
import { updateAllDayWellExpanded } from '../../store/actions/updateAllDayWellExpanded';
import { useWindowEvent } from 'owa-react-hooks/lib/useWindowEvent';

export interface AllDayCollapseHooksProps {
    store: CalendarSurfaceStore;
    collapseOnFocus: boolean;
}

export default function useAllDayCollapseHooks(props: AllDayCollapseHooksProps) {
    let parentElementRef = null as React.MutableRefObject<HTMLElement>;

    const fireCallback = React.useCallback(
        (ev: React.SyntheticEvent<HTMLElement> | UIEvent | null) => {
            if (parentElementRef.current && props.store.isAllDayWellExpanded) {
                if (ev && (ev.type === 'click' || ev.type === 'mousedown')) {
                    ev.stopPropagation();
                    ev.preventDefault();
                }

                if (ev?.type === 'focus' && !props.collapseOnFocus) {
                    return;
                }

                const source = ev
                    ? (ev.type as
                          | 'scroll'
                          | 'click'
                          | 'mousedown'
                          | 'touchstart'
                          | 'focus'
                          | 'resize')
                    : 'Initialize';

                updateAllDayWellExpanded(props.store, false, source);
            }
        },
        [props.store?.isAllDayWellExpanded, props.collapseOnFocus, parentElementRef]
    );

    useWindowEvent('resize', fireCallback);

    React.useEffect(() => {
        fireCallback(null);
    }, [parentElementRef]);

    return React.useCallback(
        (ref: React.MutableRefObject<HTMLElement>) => {
            parentElementRef = ref;

            return {
                children: null,
                handlers: {
                    onScroll: fireCallback,
                    onClick: fireCallback,
                    onMouseDownCapture: fireCallback,
                    onTouchStart: fireCallback,
                    onFocusCapture: fireCallback,
                },
            };
        },
        [fireCallback]
    );
}
